import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { m as menuListConfig, i as isEqual_1 } from './menuListConfig.js';
import { Z as Z_INDEX } from './consts.js';
import { v as getHighestContainerInDOM } from './utils.js';
import { d as defineCustomElement$3 } from './wpp-action-button2.js';
import { d as defineCustomElement$2 } from './wpp-icon-cross2.js';
import { d as defineCustomElement$1 } from './wpp-spinner2.js';

const wppPopoverCss = ":host{display:-ms-inline-flexbox;display:inline-flex}:host .anchor{width:-webkit-fit-content;display:-ms-inline-flexbox;display:inline-flex}:host .content.hidden{position:absolute;z-index:-1;opacity:0}:host slot{display:block}";

const WppPopover = /*@__PURE__*/ proxyCustomElement(class WppPopover extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.isTriggerEnabled = () => {
      // Checks if the trigger element is enabled or disabled.
      const triggerEl = this.host?.querySelector('[slot="trigger-element"]');
      if (!triggerEl)
        return false;
      if ((triggerEl?.hasAttribute('disabled') && triggerEl?.getAttribute('disabled') !== 'false') ||
        triggerEl?.classList.contains('disabled')) {
        return false;
      }
      return true;
    };
    this.createTippyInstance = () => {
      const slotContent = this.host.children[1];
      if (slotContent) {
        this.contentEl?.append(slotContent);
      }
      if (this.anchorEl && this.contentEl) {
        this.tippyInstance = menuListConfig({
          anchor: this.anchorEl,
          content: this.contentEl,
          zIndex: Z_INDEX.POPOVER,
          duration: [300, 300],
          triggerElementWidth: false,
          trigger: 'click',
          maxWidth: 'none',
          hideOnClick: false,
          popperOptions: {
            ...this.config?.popperOptions,
            modifiers: [
              ...(this.config?.popperOptions?.modifiers || []),
              {
                name: 'flip',
                options: {
                  fallbackPlacements: ['top'],
                },
              },
            ],
          },
          onClickOutside: (_, event) => {
            if (this.shouldCloseOnOutsideClick(event)) {
              this.tippyInstance.hide();
            }
          },
          appendTo: () => getHighestContainerInDOM(),
          ...this.config,
          onShow: (instance) => {
            if (!this.isTriggerEnabled())
              return false;
            if (this.dropdownWidth !== 'auto') {
              instance.popper.style.width = this.dropdownWidth;
            }
            if (this.config.onShow) {
              this.config.onShow(instance);
            }
          },
        });
      }
    };
    this.removeDisabledTag = () => {
      if (this.anchorEl?.getAttribute('disabled') === 'false') {
        this.anchorEl.removeAttribute('disabled');
      }
    };
    this.handleCrossButtonClick = () => this.tippyInstance.hide();
    this.hostCssClasses = () => ({
      'wpp-popover': true,
    });
    this.contentCssClasses = () => ({
      content: true,
      'wpp-popover-content': true,
      [`${this.externalClass}`]: true,
      hidden: this.hidden,
    });
    this.hidden = true;
    this.config = {};
    this.shouldCloseOnOutsideClick = () => true;
    this.closable = false;
    this.externalClass = '';
    this.dropdownWidth = 'auto';
  }
  /**
   * Method for closing the popover programatically
   */
  async closePopover() {
    this.tippyInstance.hide();
  }
  /**
   * Method for opening the popover programatically
   */
  async openPopover() {
    setTimeout(() => {
      this.tippyInstance.show();
    }, 0);
  }
  updateConfig(newConfig, oldConfig) {
    if (!isEqual_1(newConfig, oldConfig)) {
      this.config = newConfig;
      this.tippyInstance?.setProps(newConfig);
    }
  }
  componentDidLoad() {
    setTimeout(() => {
      this.createTippyInstance();
      this.hidden = false;
    }, 0);
    this.mutationObserver = new MutationObserver(() => {
      this.removeDisabledTag();
    });
    this.startObserving();
  }
  disconnectedCallback() {
    this.tippyInstance?.destroy();
    this.mutationObserver?.disconnect();
  }
  connectedCallback() {
    if (this.tippyInstance?.state.isDestroyed) {
      this.createTippyInstance();
    }
    if (this.mutationObserver) {
      this.startObserving();
    }
  }
  startObserving() {
    this.mutationObserver.observe(this.host?.children[0], { attributes: true });
  }
  render() {
    return (h(Host, { class: this.hostCssClasses(), exportparts: "anchor, trigger-element" }, h("div", { class: "anchor", part: "anchor", ref: anchorEl => (this.anchorEl = anchorEl) }, h("slot", { name: "trigger-element", part: "trigger-element" })), h("div", { class: this.contentCssClasses(), part: "content", ref: contentEl => (this.contentEl = contentEl) }, this.closable && (h("wpp-action-button-v2-21-0", { onClick: this.handleCrossButtonClick, class: "cross-button", variant: "secondary" }, h("wpp-icon-cross-v2-21-0", { slot: "icon-end" }))), h("slot", null))));
  }
  static get registryIs() { return "wpp-popover-v2-21-0"; }
  get host() { return this; }
  static get watchers() { return {
    "config": ["updateConfig"]
  }; }
  static get style() { return wppPopoverCss; }
}, [1, "wpp-popover", "wpp-popover-v2-21-0", {
    "config": [1040],
    "shouldCloseOnOutsideClick": [16],
    "closable": [4],
    "externalClass": [1, "external-class"],
    "dropdownWidth": [513, "dropdown-width"],
    "hidden": [32],
    "closePopover": [64],
    "openPopover": [64]
  }]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["wpp-popover-v2-21-0", "wpp-action-button-v2-21-0", "wpp-icon-cross-v2-21-0", "wpp-spinner-v2-21-0"];
  components.forEach(tagName => { switch (tagName) {
    case "wpp-popover-v2-21-0":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, WppPopover);
      }
      break;
    case "wpp-action-button-v2-21-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "wpp-icon-cross-v2-21-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
    case "wpp-spinner-v2-21-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$1();
      }
      break;
  } });
}

export { WppPopover as W, defineCustomElement as d };
